import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useOrder } from "@/api/mutations";
import { HelpTip } from "@/components/HelpTip";
import { ArrowRightIcon, CheckIcon, LayersIcon } from "@/components/icons";
import { Button } from "@/components/Button";
import { FulfilmentTimeline } from "@/components/FulfilmentTimeline";
import { cn } from "@/lib/cn";

// The /api/v1/orders/:id response carries the fulfilment lifecycle fields;
// widen the hook's shape locally until the shared OrderDetail type is updated.
type OrderFulfilment = {
  royalMailTracking?: string | null;
  factoryReceivedAt?: string | null;
  packedAt?: string | null;
  dispatchedAt?: string | null;
};

/**
 * Order placed — step 3 of 3. Confirmation hero, pre-press / production /
 * dispatch timeline, and a summary panel with the real order from the API.
 * The order ID is passed via ?orderId=… by the Approval screen after a
 * successful POST /api/v1/orders.
 */
export function OrderConfirmationPage() {
  const { kitId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const orderId = params.get("orderId") ?? undefined;
  const { data: order, isLoading, isError } = useOrder(orderId);

  if (!orderId) {
    return (
      <div className="mx-auto max-w-3xl px-8 py-12 text-center">
        <h1 className="mb-2 text-2xl font-bold">Order not found</h1>
        <p className="mb-6 text-fg-3">No order ID in the URL.</p>
        <Link to="/catalogue" className="nhsuk-button nhsuk-button--secondary">
          Back to catalogue
        </Link>
      </div>
    );
  }
  if (isLoading) return <div className="mx-auto max-w-7xl px-8 py-10 text-fg-3">Loading order…</div>;
  if (isError || !order)
    return (
      <div className="mx-auto max-w-3xl px-8 py-12 text-center">
        <h1 className="mb-2 text-2xl font-bold">Couldn't load order</h1>
        <p className="mb-6 text-fg-3">Server didn't return the order details. Try again from /orders.</p>
        <Link to="/orders" className="nhsuk-button nhsuk-button--secondary">
          Open My orders
        </Link>
      </div>
    );

  const orderNo = order.id;
  const qty = order.qty;
  const today = new Date();
  const fulfilment = order as typeof order & OrderFulfilment;

  return (
    <div className="mx-auto max-w-[1100px] px-8 pb-16 pt-12">
      <header className="mb-10 text-center">
        <div
          className="mx-auto mb-4 grid h-16 w-16 place-items-center rounded-full text-white"
          style={{ background: "var(--ok)" }}
        >
          <CheckIcon width={32} height={32} strokeWidth={2.5} />
        </div>
        <h1 className="m-0 mb-2 text-2xl font-bold tracking-tight text-fg-1">
          Order placed
        </h1>
        <p className="m-0 mb-3 font-mono text-sm uppercase tracking-wide tabular-nums text-fg-3">
          Order {orderNo} ·{" "}
          {today.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
        </p>
        <p className="mx-auto m-0 mb-6 max-w-[540px] text-base leading-relaxed text-fg-2">
          Your branded {order.kitTitle.toLowerCase()} kits are heading to print. We've emailed a
          proof PDF and order confirmation to your practice manager. You'll get a tracked
          dispatch notice when they leave the kitting facility.
        </p>
        {order.status === "hold" && order.holdReason && (
          <p className="mx-auto m-0 mb-6 max-w-[540px] rounded-md border-l-[3px] border-[var(--admin)] bg-[var(--admin-tint)] px-4 py-3 text-left text-sm text-[var(--admin-dark)]">
            <strong>On hold:</strong> {order.holdReason}. Your account manager will release this
            order to production once approved.
          </p>
        )}
      </header>

      <div className="mb-8 rounded-lg border border-[var(--border-1)] bg-white p-5">
        <div className="mb-4 flex items-center gap-1.5">
          <h3 className="m-0 text-sm font-semibold text-fg-1">Fulfilment</h3>
          <HelpTip content="We render the proof and start the print run once your order is placed. You'll see each stage update here — and a Royal Mail return tracking code appears once the kits are dispatched." />
        </div>
        <FulfilmentTimeline
          status={order.status}
          royalMailTracking={fulfilment.royalMailTracking}
          timestamps={{
            placedAt: order.placedAt,
            factoryReceivedAt: fulfilment.factoryReceivedAt,
            packedAt: fulfilment.packedAt,
            dispatchedAt: fulfilment.dispatchedAt,
          }}
        />
      </div>

      <div className="overflow-hidden rounded-lg border border-[var(--border-1)] bg-white">
        <div className="flex items-center justify-between border-b border-[var(--border-1)] bg-bg-1 px-5 py-3">
          <h3 className="m-0 text-sm font-semibold text-fg-1">Order summary</h3>
          <span className="font-mono text-xs text-fg-3">{orderNo}</span>
        </div>
        <div className="grid gap-6 p-5 sm:grid-cols-2">
          <div>
            <Eyebrow>Product</Eyebrow>
            <div className="text-base font-semibold text-fg-1">{order.kitTitle}</div>
            <div className="mt-0.5 font-mono text-xs tabular-nums text-fg-3">
              {order.kitCode} · {qty} units
            </div>

            <Eyebrow className="mt-4">Brand profile</Eyebrow>
            <div className="text-sm font-medium text-fg-1">{order.brandProfileName}</div>
            <div className="text-xs text-fg-3">{order.brandProfileTagline}</div>
          </div>
          <div>
            <Eyebrow>Deliver to</Eyebrow>
            <div className="text-sm leading-relaxed text-fg-1">
              {order.tenantName}<br />
              1 Example Street<br />
              London EC1A 1AA<br />
              <span className="text-fg-3">Attn: practice manager</span>
            </div>
            <Eyebrow
              className="mt-4"
              help="This is the practice's NHS Procurement Shared Services (PSP) account number. Charges go to a single account today; per-cost-centre allocation is on the roadmap for a follow-up release."
            >
              Charged to
            </Eyebrow>
            <div className="text-sm text-fg-1">Account DEMO-001 · NHS PSP terms</div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t border-[var(--border-1)] bg-bg-1 px-4 py-3">
          <Link
            to="/designs"
            className={cn(
              "inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[var(--border-2)] bg-white px-4 text-sm font-semibold text-fg-1 no-underline transition-colors",
              "hover:bg-[var(--bg-1)] active:bg-[var(--bg-2)]",
              "outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)] focus-visible:ring-offset-1",
            )}
          >
            <LayersIcon width={14} height={14} /> View all designs
          </Link>
          <Button variant="secondary">Clone this order</Button>
          <div className="flex-1" />
          <Button
            onClick={() => navigate("/catalogue")}
            trailingIcon={<ArrowRightIcon />}
            className="bg-[var(--studio)] hover:bg-[var(--studio-dark)] active:bg-[var(--studio-dark)]"
          >
            Order another kit
          </Button>
        </div>
      </div>
    </div>
  );
}

function Eyebrow({
  children,
  className,
  help,
}: {
  children: React.ReactNode;
  className?: string;
  help?: string;
}) {
  return (
    <div
      className={cn(
        "mb-1.5 flex items-center gap-1.5 text-eyebrow font-semibold uppercase text-fg-mute",
        className,
      )}
    >
      <span>{children}</span>
      {help && <HelpTip content={help} />}
    </div>
  );
}
